/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Tooltip } from "primereact/tooltip";
import {
  accessAPI,
  Boton,
  CardTotalesPendientes,
  conIva,
  formatMonto,
  obtenerArchivo,
  TablaFiltrable,
  tituloBase,
  Titulo,
  useIva
} from "../../shared";

import { comprobantesFactura, descripcionFactura, estadoFactura, fechaCorta } from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import SkeletonListado from "../SkeletonListado";

// Tipo que devuelve la API → extensión del nombre con que se guarda
const EXTENSION_POR_TIPO = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx"
};

export default function Facturacion() {
  const iva = useIva();
  const toast = useRef(null);
  const menuRef = useRef(null);
  const [facturas, setFacturas] = useState(null);
  const [seleccionada, setSeleccionada] = useState(null);
  const [enProceso, setEnProceso] = useState(null);

  useEffect(() => {
    document.title = "Facturación";
    accessAPI(
      "GET",
      "cliente/facturacion",
      null,
      (r) =>
        setFacturas(
          r.map((f) => ({
            ...f,
            _descripcion: descripcionFactura(f),
            _estado: estadoFactura(f),
            // Para el desplegable de estado de la tabla
            _estadoTexto: estadoFactura(f).texto,
            _numero: f.numeroFactura || `#${f.id}`
          }))
        ),
      (r) => {
        setFacturas([]);
        toast.current?.show({ severity: "error", summary: "Error", detail: r[0]?.msg || "Error al cargar facturas", sticky: true });
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  // El comprobante de Biller es siempre PDF, pero el que subió a mano un
  // organismo sin Biller puede ser una imagen u otro documento: la extensión
  // del nombre sale del tipo que devuelve la API.
  const nombreComprobante = (factura, prefijo, blob) =>
    `${prefijo}-${factura.numeroFactura || factura.id}.${EXTENSION_POR_TIPO[blob?.type] || "pdf"}`;

  const descargar = (factura, ruta, prefijo, error) => {
    setEnProceso(factura.id);
    obtenerArchivo(
      `cliente/facturacion/${factura.id}/${ruta}`,
      (blob) => {
        setEnProceso(null);
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = nombreComprobante(factura, prefijo, blob);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      },
      (r) => {
        setEnProceso(null);
        toast.current?.show({ severity: "error", summary: "Error", detail: r[0]?.msg || error, sticky: true });
      }
    );
  };

  // Cada opción solo si existe el comprobante (en Biller o subido a mano)
  const hay = seleccionada ? comprobantesFactura(seleccionada) : {};
  const menuItems = seleccionada
    ? [
        {
          label: "Descargar factura",
          icon: "pi pi-download",
          disabled: !hay.factura,
          command: () => descargar(seleccionada, "pdf", "Factura", "Error al descargar factura")
        },
        ...(hay.recibo
          ? [
              {
                label: "Descargar comprobante de pago",
                icon: "pi pi-download",
                command: () => descargar(seleccionada, "recibo", "Recibo", "Error al descargar el comprobante de pago")
              }
            ]
          : []),
        ...(hay.notaCredito
          ? [
              {
                label: "Descargar nota de crédito",
                icon: "pi pi-download",
                command: () =>
                  descargar(seleccionada, "notacredito", "NotaDeCredito", "Error al descargar nota de crédito")
              }
            ]
          : [])
      ]
    : [];

  const monto = (f) => {
    const simbolo = f.moneda?.simbolo || "";
    const base = `${simbolo} ${formatMonto(f.monto)}`;
    if (!f.masIva) return base;
    const total = formatMonto(Number(conIva(f.monto, iva).toFixed(2)));
    return (
      <>
        <Tooltip target={`.monto-iva-${f.id}`} />
        <span className={`monto-iva-${f.id}`} data-pr-tooltip={`Total con IVA: ${simbolo} ${total}`} data-pr-position="top">
          {base} + IVA
        </span>
      </>
    );
  };

  // Menú de descargas de la fila; mientras descarga, el botón muestra el loader
  const acciones = (f) => (
    <Boton
      icono
      chico
      negro
      loader={enProceso === f.id}
      callback={(e) => {
        e.stopPropagation();
        setSeleccionada(f);
        menuRef.current.toggle(e);
      }}
    >
      <i className="pi pi-ellipsis-v" />
    </Boton>
  );

  // Hasta tener los datos, el esqueleto de la sección (como en el admin)
  // (con el Toast, en el mismo lugar que después, por si falla la carga)
  if (!facturas)
    return (
      <>
        <Toast ref={toast} position="top-center" />
        <SkeletonListado filtros={1} cardArriba />
      </>
    );

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        subtitulo="Tus facturas, su estado de pago y los comprobantes."
      >
        Facturación
      </Titulo>
      <Menu model={menuItems} popup ref={menuRef} />

      {/* Deuda resumida o "todas pagas", el mismo resumen que en el admin */}
      <CardTotalesPendientes facturas={facturas} />

      <Card css={{ marginBottom: "1em" }}>
        {facturas?.length === 0 ? (
          <div css={{ textAlign: "center", padding: "2em 1em", color: "var(--grisTextos)" }}>
            <i className="pi pi-receipt" css={{ fontSize: "1.5rem" }} />
            <div css={{ fontWeight: 600, color: "rgba(44, 44, 44, 1)", marginTop: "0.5em" }}>No hay facturas</div>
          </div>
        ) : (
          <TablaFiltrable
            // La key fuerza a redibujar el loader del botón de la fila
            key={`facturas-${enProceso}`}
            value={facturas || []}
            searchFields={["_numero", "_descripcion"]}
            filtros={[{ campo: "_estadoTexto", label: "Estado", labelPlural: "estados", siempreVisible: true }]}
            nombreArchivo="facturas"
            emptyMessage="No hay facturas"
            className="p-tabla"
          >
            <Column
              header="Número"
              body={(f) => <span className="p-celda-titulo">{f._numero}</span>}
              style={{ width: "10rem" }}
              exportValue={(f) => f._numero}
            />
            <Column
              header="Descripción"
              field="_descripcion"
            />
            <Column
              header="Fecha de emisión"
              body={(f) => fechaCorta(f.fechaCreacion)}
              style={{ width: "10rem" }}
            />
            <Column
              header="Monto"
              body={monto}
              style={{ width: "11rem" }}
              exportValue={(f) => `${f.moneda?.simbolo || ""} ${formatMonto(f.monto)}${f.masIva ? " + IVA" : ""}`}
            />
            <Column
              header="Estado"
              body={(f) => (
                <>
                  <Tag severity={f._estado.severidad}>{f._estado.texto}</Tag>
                  {f.fechaPago && f._estado.clave === "pagada" && (
                    <div className="p-celda-sub" css={{ marginTop: 4 }}>
                      El {fechaCorta(f.fechaPago)}
                    </div>
                  )}
                </>
              )}
              style={{ width: "11rem" }}
              exportValue={(f) => f._estadoTexto}
            />
            <Column body={acciones} style={{ width: "5rem", textAlign: "center" }} />
          </TablaFiltrable>
        )}
      </Card>
    </>
  );
}
